import { DiContainer } from '@jkhy/frontend-utils/di';
import { PlatformAppService } from '@jkhy/platform-tools/services/platform-app-service';
import { ConfigurationService } from '@treasury/core/config/configuration.service';
import { AUTH_TOKEN } from '@treasury/core/http/request-validation';
import { LoggingService } from '@treasury/core/logging';
import { EntitlementsService } from '@treasury/domain/entitlements/entitlements.service';
import { AuthenticationService } from '@treasury/domain/services/authentication/authentication.service';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { Commands, Context, RedirectResult } from '@vaadin/router';

/**
 * Authenticates user and checks route access. On success, returns undefined. On failure, redirects to the provided authFailRedirect or login page
 * @param context the Vaadin router context object, used for route information and params. Vaadin injects a redirectFrom property on the context object during redirects but doesn't include it in the type definition. The definition has been extended here to include it for type safety.
 * @param commands the Vaadin router commands object used for navigation
 * @param authFailRedirect the route to redirect to on auth failure. Should always be a public route.
 * @returns undefined on success, or RedirectResult to the provided authFailRedirect or login page on failure
 */
export async function isAuthorized(
    context: Context & { redirectFrom?: string },
    commands: Commands,
    authFailRedirect = '/'
): Promise<RedirectResult | undefined> {
    try {
        const { pathname, redirectFrom } = context;
        const authService = await AuthenticationService.getInstance();
        const config = await ConfigurationService.getInstance();
        const hasToken = sessionStorage.getItem(AUTH_TOKEN);
        const di = await DiContainer.getInstance();
        if (!authService.authenticated) {
            if (config.bannoAuthEnabled) {
                /**
                 * If banno auth is enabled, validate the eauth cookie,
                 * generate the jha auth token and set it in session storage.
                 */
                config.institutionId = context.params.institutionId.toString();
                await getTokenFromJhIdentity(di);
            } else if (hasToken) {
                /**
                 * If a user token exists, attempt to authenticate the user on the token.
                 * If authentication fails, we clear the token from session storage and redirect to login on the next step.
                 */
                await authService.authenticate();
                if (!authService.authenticated) {
                    return commands.redirect(authFailRedirect);
                }
            } else {
                /**
                 * No token and no banno auth — redirect to login while preventing infinite redirects.
                 */
                if (pathname !== redirectFrom) {
                    return commands.redirect(authFailRedirect);
                }
                return undefined;
            }
        }

        const platformService = await di.getAsync(PlatformAppService);
        const entitlementService = await di.getAsync(EntitlementsService);
        await entitlementService.init();

        // `checkRoute` pings `/a/auth/api/session/expirations` via the platform
        // admin-user service. That endpoint only exists in banno-auth deployments;
        // in token-auth mode it 404s. When banno auth is disabled the platform
        // service is already an unauthenticated app (its constructor marks the user
        // unauthenticated) and `requiresInstitutionId` is false, so `checkRoute`
        // would only make the failing call with no other effect — skip it.
        if (config.bannoAuthEnabled) {
            await platformService.checkRoute(pathname);
        }
    } catch (error) {
        // In the event of an error during auth or entitlement checks,
        // we want to invalidate the session and redirect to login
        const di = await DiContainer.getInstance();
        const loggingService = await di.getAsync(LoggingService);
        loggingService.logError(
            'An error occurred during authentication',
            error instanceof Error ? error : new Error(String(error))
        );

        di.getAsync(TmToastService).then(toastService => {
            toastService.show(
                'An error occurred during authentication. Logging out.',
                TmToastPosture.Danger
            );
        });

        const authService = await AuthenticationService.getInstance();
        authService.invalidate();

        return commands.redirect(authFailRedirect);
    }

    return undefined;
}

/**
 * This function is responsible for validating the jha identity cookie,
 * generating a jha auth token and setting it in session storage
 */
export const getTokenFromJhIdentity = async (di: DiContainer) => {
    const config = await di.getAsync(ConfigurationService);
    const authService = await di.getAsync(AuthenticationService);

    const validated = await authService.validate(config.institutionId);

    if (!validated.success) {
        // If validation fails, we throw an error to be caught in the calling function, which will log the error and redirect to login
        throw new Error('Failed to validate JHA identity');
    }

    sessionStorage.setItem(AUTH_TOKEN, validated.token);
    if (!authService.authenticated) {
        await authService.authenticate();
    }
};
